import { ErrorCode } from '@sb-protogen/external/external_control';

interface FaultCatalogEntry {
  message: string;
  /**
   * Can an operator act on this — pick a different target/path, wait, or do
   * something at the robot? A caller showing text to an operator (not a
   * program branching on `code`) should fall back to something generic when
   * this is false, instead of pasting engineering detail into a toast.
   */
  operatorActionable: boolean;
}

/**
 * Every customer-facing `Fault.message`, in one place — plus, per code,
 * whether an operator can act on it.
 *
 * This library owns all of them, including for codes authored elsewhere in the
 * stack. A second copy of one of these judgments somewhere else is exactly how
 * the two get to disagree, and the disagreement is only visible to a customer.
 *
 * Rules for `message`, in order:
 *
 * 1. **No service names.** A customer does not know what botman or
 *    arm-control-bot are, and telling them is not information.
 * 2. **No timings, thresholds or internal identifiers.** Those change with
 *    tuning; a message that quotes one becomes a lie after a config push.
 * 3. **State what is true, not what is likely.** When the only source of truth
 *    about the controller is unreachable, the honest statement is that its
 *    status is unknown — not that it has failed. The robot may be fine.
 *
 * `Fault.field` is always empty on this surface and `message` can only be a
 * per-code constant: the structured detail behind a trajectory-controller fault
 * lives in the controller's debug path and never leaves it.
 *
 * The operator-facing UI says different things for some of these conditions
 * ("Internal communication failure", "Error: Botman communication.") and that
 * divergence is deliberate — those strings are for someone standing at the
 * robot, these are for a program talking to it over an API.
 */
const FAULT_CATALOG: Record<ErrorCode, FaultCatalogEntry> = {
  [ErrorCode.ERROR_CODE_UNSPECIFIED]: {
    message: 'Unspecified fault',
    operatorActionable: false,
  },

  // --- Trajectory-controller faults -----------------------------------------
  // Every one of these arrives through the controller's error entry, which
  // clears the queue and fast-stops. Motion has already stopped; none of them
  // means "resend, the arm is fine".
  [ErrorCode.IN_SINGULARITY]: {
    message:
      'The requested motion passes through a singularity and cannot be followed',
    operatorActionable: true,
  },
  [ErrorCode.TRAJECTORY_OVER_LIMITS]: {
    message:
      'The requested motion exceeds the arm’s velocity or acceleration limits',
    operatorActionable: true,
  },
  [ErrorCode.JOINT_COMMAND_OUT_OF_LIMITS]: {
    message: 'A commanded joint position is outside the arm’s limits',
    operatorActionable: true,
  },
  [ErrorCode.STALENESS_TIMEOUT]: {
    message: 'No new setpoint arrived in time, so the arm stopped',
    operatorActionable: false,
  },
  [ErrorCode.INVALID_TRAJECTORY]: {
    message: 'The submitted trajectory is not executable',
    operatorActionable: false,
  },
  [ErrorCode.MALFORMED_TARGET]: {
    message: 'A streamed setpoint was structurally invalid',
    operatorActionable: false,
  },
  [ErrorCode.COMPUTATION]: {
    message: 'The motion could not be computed',
    operatorActionable: false,
  },
  [ErrorCode.WORKSPACE_LIMIT_VIOLATION]: {
    message: 'The requested motion leaves the arm’s allowed workspace',
    operatorActionable: true,
  },
  [ErrorCode.OVERRUN]: {
    message:
      'The controller could not keep up with its control cycle, so the arm stopped',
    operatorActionable: false,
  },
  [ErrorCode.STREAM_JUMP]: {
    message:
      'Consecutive streamed setpoints were too far apart, so the arm stopped',
    operatorActionable: false,
  },
  [ErrorCode.TRACKING_ERROR_EXCEEDED]: {
    message:
      'The streamed target moved too far from the pose the arm was tracking, so the arm stopped',
    operatorActionable: false,
  },
  [ErrorCode.ARM_NOT_READY]: {
    message: 'The arm is not ready to execute the requested motion',
    operatorActionable: true,
  },

  // --- Command-path rejections ----------------------------------------------
  // Reached through mapTcRejection when a synchronous RPC reply refuses a
  // request; see its doc comment for which paths produce them.
  [ErrorCode.QUEUE_FULL]: {
    message: 'The motion queue is full',
    operatorActionable: true,
  },
  [ErrorCode.NO_CONTROL]: {
    message: 'Another client holds control of the arm',
    operatorActionable: true,
  },
  [ErrorCode.NOT_IDLE]: {
    message: 'The arm is busy and cannot accept this request',
    operatorActionable: true,
  },
  [ErrorCode.INVALID_ARGUMENT]: {
    message: 'The request contains an invalid value',
    operatorActionable: false,
  },
  [ErrorCode.FRAME_NOT_SPECIFIED]: {
    message: 'A required reference frame was not set',
    operatorActionable: false,
  },
  [ErrorCode.FRAME_NOT_SUPPORTED]: {
    message: 'The requested reference frame is not supported',
    operatorActionable: false,
  },
  [ErrorCode.INCOMPATIBLE_FRAMES]: {
    message: 'The requested reference frames are incompatible',
    operatorActionable: false,
  },
  [ErrorCode.NO_ERROR]: { message: 'No error', operatorActionable: false },

  // --- Safety faults --------------------------------------------------------
  [ErrorCode.ROBOT_ESTOP]: {
    message:
      'The emergency stop is engaged. Release it at the robot to continue',
    operatorActionable: true,
  },
  [ErrorCode.ROBOT_POWERED_OFF]: {
    message: 'Arm power is off. Re-enable it at the robot to continue',
    operatorActionable: true,
  },
  [ErrorCode.ROBOT_COLLISION_STOP]: {
    message: 'The arm stopped because a collision was detected',
    operatorActionable: true,
  },
  [ErrorCode.ROBOT_JOINT_LIMIT]: {
    message: 'The arm stopped because a joint reached its limit',
    operatorActionable: true,
  },
  [ErrorCode.ROBOT_PROTECTIVE_STOP]: {
    message: 'The arm stopped because a safety limit was exceeded',
    operatorActionable: true,
  },

  // --- Generic / cross-cutting ---------------------------------------------
  [ErrorCode.TC_CONTROLLER_ERROR]: {
    message:
      'The motion controller is in an error state and the arm has stopped',
    operatorActionable: false,
  },
  [ErrorCode.CLIENT_STREAM_TIMEOUT]: {
    message: 'The command stream stopped, so the streaming session ended',
    operatorActionable: false,
  },
  // Pinned here and reused wherever this code is authored. "Unknown", not
  // "broken": what has been lost is our source of truth about the controller,
  // not necessarily the controller.
  [ErrorCode.CONTROLLER_UNAVAILABLE]: {
    message: 'Controller status unknown — communication lost',
    operatorActionable: false,
  },

  [ErrorCode.UNRECOGNIZED]: {
    message: 'Unrecognized fault',
    operatorActionable: false,
  },
};

/**
 * Base for every `docs_url`. The anchor is derived mechanically from the code
 * name (see {@link faultDocsUrl}) so a new `ErrorCode` cannot ship without a
 * link, and the link is stable because the enum is append-only.
 */
export const FAULT_DOCS_BASE_URL =
  'https://docs.standardbots.com/docs/external-control/errors';

function catalogEntry(code: ErrorCode): FaultCatalogEntry {
  return FAULT_CATALOG[code] ?? FAULT_CATALOG[ErrorCode.UNRECOGNIZED];
}

/** The customer-facing message for a code. */
export function faultMessage(code: ErrorCode): string {
  return catalogEntry(code).message;
}

/** See {@link FaultCatalogEntry.operatorActionable}. */
export function isOperatorActionable(code: ErrorCode): boolean {
  return catalogEntry(code).operatorActionable;
}

/**
 * `ErrorCode` enumerator name, lower-cased with underscores hyphenated, as a
 * fragment on the errors page: `WORKSPACE_LIMIT_VIOLATION` →
 * `…/errors#workspace-limit-violation`.
 */
export function faultDocsUrl(code: ErrorCode): string {
  const name = ErrorCode[code] ?? ErrorCode[ErrorCode.UNRECOGNIZED];
  const anchor = name.toLowerCase().replace(/_/g, '-');

  return `${FAULT_DOCS_BASE_URL}#${anchor}`;
}
